// packages/goodlaw/jurisdictionFromQuestion.js — RUN CK2 AMENDMENT §2.2.
//
// ═══════════════════════════════════════════════════════════════════════════════════════════════
// 🔴 A JURISDICTION THE USER STATED — NEVER ONE INFERRED FROM CONTEXT.
//
// "In NJ, …" and "Under New Jersey law, …" are the user telling us the forum. "What did the New
// Jersey Turnpike Authority hold about discovery?" is a PARTY that happens to contain a state name,
// and scoping on it would silently drop every authority the user actually needs. So this matches on
// POSITION AND CONSTRUCTION, never on the presence of the word.
//
// 🔴 IT LIVES HERE BECAUSE THE VOCABULARY DOES. RUN SC counted eight type vocabularies across three
// apps; a ninth for jurisdictions is exactly what this must not become. `jurisdictions.js` in this
// same package is the ONE list — it is what `packages/ui/react/JurisdictionPicker.jsx:41` builds its
// ENTRY map from and what `apps/steel/server/lib/scope.js:22` parses scope tokens against. This
// module imports that list and returns one of ITS ids, so a detected jurisdiction and a picked one
// are the same value by construction and cannot drift.
//
// ═══════════════════════════════════════════════════════════════════════════════════════════════
// THE RULE, in one line: a leading prepositional frame, then the jurisdiction, then a boundary that
// proves it was a FORUM and not a modifier.
//
//     (start of the question, or the start of a clause)
//     "in" | "under"
//     the jurisdiction, as its full name ("New Jersey"), its postal code ("NJ"), or its
//     Bluebook abbreviation ("N.J.")
//     followed by a comma, OR by a legal-domain noun — law/laws/rule/rules/statute/statutes/
//     code/court/courts/procedure
//
// ⚠️ THE BOUNDARY IS THE WHOLE DEFENCE. "In California, what is …" ends the frame with a comma.
// "Under New Jersey law, …" ends it with a domain noun. "the New Jersey Turnpike Authority" has no
// preposition in front of it, and "In California Public Utilities Commission filings" continues
// into a capitalised proper noun rather than a comma or a domain noun — both are refused.
//
// ⚠️ "in the California …" is refused for free: the frame requires the jurisdiction to follow the
// preposition IMMEDIATELY, and an article breaks it. That is deliberate — "in the California
// Supreme Court" is a court selection, not a jurisdiction statement, and the picker is where a
// court is chosen.
//
// 🔴 RETURNS AN ID OR null. It never returns a guess and never returns a partial match. The caller
// is required (AMENDMENT §3) to SHOW what was detected in the picker rather than scope invisibly.
import { STATES, TERRITORIES } from './jurisdictions.js';

/** Bluebook abbreviations carry periods; a user typing one rarely does. Compared without them. */
const flat = (s) => String(s || '').toLowerCase().replace(/\./g, '');

/* id → the strings that may stand for it, longest first so "New Jersey" is tried before "New". */
const FORMS = (() => {
  const out = [];
  for (const j of [...STATES, ...TERRITORIES]) {
    const forms = new Set([flat(j.name), flat(j.abbr), flat(j.id)]);
    for (const f of forms) if (f) out.push({ id: j.id, form: f });
  }
  return out.sort((a, b) => b.form.length - a.form.length);
})();

/** The domain nouns that end a frame as convincingly as a comma does. */
const DOMAIN = 'law|laws|rule|rules|statute|statutes|code|codes|court|courts|procedure|precedent';

/**
 * The jurisdiction the QUESTION STATES, or null.
 *
 * @param {string} question
 * @returns {string|null} an id from jurisdictions.js ('nj'), never a display string
 */
export function jurisdictionFromQuestion(question) {
  const q = String(question || '');
  if (!q.trim()) return null;
  /* 🔴 SINGLE-LETTER ABBREVIATION PERIODS COLLAPSE BEFORE THE CLAUSE SPLIT, and the order is the
     whole of this line. Splitting sentences on "." first tears "N.J." into "In N" / "J" / ", what
     …" and the frame can never match — measured: `In N.J., what is the rule?` returned null while
     the identical question spelled "NJ" returned 'nj'. Only a ONE-letter token loses its period, so
     a real sentence end ("… the rule.") is untouched. */
  const norm = q.replace(/\b([A-Za-z])\./g, '$1');
  // Clause starts: the beginning of the question, or immediately after . ; ? or a newline.
  const clauses = norm.split(/(?:^|[.;?\n])\s*/).filter(Boolean);
  for (const clause of clauses) {
    const head = flat(clause).replace(/[^a-z0-9\s,]/g, ' ').replace(/\s+/g, ' ').trim();
    for (const { id, form } of FORMS) {
      // preposition, the form, then a comma or a domain noun — anchored at the clause head.
      const re = new RegExp(`^(?:in|under)\\s+${form.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*(?:,|(?:${DOMAIN})\\b)`);
      if (re.test(head)) return id;
    }
  }
  return null;
}

export default { jurisdictionFromQuestion };
